// src/utils/crossProfileImport.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const TOKEN_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes
const QUERY_PARAM_NAME = '_cpi'; // Cross-Profile Import

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Creates an encrypted import token for cross-profile entity loading
 * Token contains: entityId, sourceProfile, targetProfile, timestamp
 *
 * @param {Object} data - Token data
 * @param {string} data.entityId - The entity ID to import
 * @param {string[]} data.sourceProfile - Current entityProfile of the entity (e.g., ['customer'])
 * @param {string[]} data.targetProfile - Target entityProfile of the module (e.g., ['company'])
 * @returns {string} Base64 encoded token
 */
function createImportToken(data) {
  const payload = {
    eid: data.entityId,
    sp: data.sourceProfile,
    tp: data.targetProfile,
    ts: Date.now(),
    v: 1, // version for future compatibility
  };

  const jsonStr = JSON.stringify(payload);
  // Use Base64 encoding (URL safe)
  const base64 = btoa(jsonStr)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  return base64;
}

/**
 * Parses and validates an import token
 *
 * @param {string} token - The Base64 encoded token
 * @returns {Object|null} Parsed token data or null if invalid/expired
 */
function parseImportToken(token) {
  if (!token || typeof token !== 'string') return null;

  try {
    // Restore Base64 padding
    let base64 = token.replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4;
    if (padding) {
      base64 += '='.repeat(4 - padding);
    }

    const jsonStr = atob(base64);
    const payload = JSON.parse(jsonStr);

    // Validate structure
    if (!payload.eid || !payload.ts || payload.v !== 1) {
      return null;
    }

    // Check expiry
    if (Date.now() - payload.ts > TOKEN_EXPIRY_MS) {
      console.warn('[crossProfileImport] Token expired');
      return null;
    }

    return {
      entityId: payload.eid,
      sourceProfile: payload.sp || [],
      targetProfile: payload.tp || [],
      timestamp: payload.ts,
    };
  } catch (error) {
    console.warn('[crossProfileImport] Failed to parse token:', error);
    return null;
  }
}

/**
 * Checks if a token is valid (exists and not expired)
 *
 * @param {string} token - The token to validate
 * @returns {boolean} True if valid
 */
function isValidImportToken(token) {
  return parseImportToken(token) !== null;
}

/**
 * Gets the query parameter name used for import tokens
 *
 * @returns {string} The query parameter name
 */
function getQueryParamName() {
  return QUERY_PARAM_NAME;
}

/**
 * Extracts import token from URL search params
 *
 * @param {URLSearchParams|string} searchParams - URL search params or query string
 * @returns {string|null} The token or null
 */
function getImportTokenFromUrl(searchParams) {
  if (typeof searchParams === 'string') {
    const params = new URLSearchParams(searchParams);
    return params.get(QUERY_PARAM_NAME);
  }
  if (searchParams instanceof URLSearchParams) {
    return searchParams.get(QUERY_PARAM_NAME);
  }
  return null;
}

/**
 * Builds a URL with the import token
 *
 * @param {string} baseUrl - The base URL
 * @param {string} token - The import token
 * @returns {string} URL with token as query param
 */
function buildUrlWithToken(baseUrl, token) {
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${QUERY_PARAM_NAME}=${token}`;
}

/**
 * Removes the import token from URL (after successful save)
 *
 * @param {string} url - The URL to clean
 * @returns {string} URL without the import token
 */
function removeTokenFromUrl(url) {
  try {
    const urlObj = new URL(url, window.location.origin);
    urlObj.searchParams.delete(QUERY_PARAM_NAME);
    return urlObj.pathname + urlObj.search;
  } catch {
    // Fallback for relative URLs
    return url
      .replace(new RegExp(`[?&]${QUERY_PARAM_NAME}=[^&]*`), '')
      .replace(/\?$/, '');
  }
}

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export {
  createImportToken,
  parseImportToken,
  isValidImportToken,
  getQueryParamName,
  getImportTokenFromUrl,
  buildUrlWithToken,
  removeTokenFromUrl,
  QUERY_PARAM_NAME,
};
